import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Email, fetchEmailById, updateEmailStatus } from '../../services/emailService';
import '../../styles/AccountTools.css';

const EmailDetails = () => {
  const { emailId } = useParams();
  const { user } = useAuth();
  const userId = user?.id || '';
  const [email, setEmail] = useState<Email | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const loadEmail = useCallback(async () => {
    if (!emailId || !userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchEmailById(emailId, userId);
      setEmail(data);
      if (data.status === 'UNREAD') {
        await updateEmailStatus(emailId, userId, 'READ').catch(() => undefined);
      }
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Failed to load email.');
    } finally {
      setLoading(false);
    }
  }, [emailId, userId]);

  useEffect(() => {
    loadEmail();
  }, [loadEmail]);

  if (!userId) {
    return (
      <main className="tool-page">
        <div className="tool-card tool-empty">Sign in to view email details.</div>
      </main>
    );
  }

  return (
    <main className="tool-page">
      <div className="tool-header">
        <div>
          <h1>Email Details</h1>
          <p>{emailId}</p>
        </div>
        <Link className="tool-btn-secondary" to="/account/inbox">Back to Inbox</Link>
      </div>

      {message && <div className="tool-message error">{message}</div>}

      {loading || !email ? (
        <div className="tool-loading">Loading email...</div>
      ) : (
        <section className="tool-card">
          <h2>{email.title || 'No subject'}</h2>
          <div className="tool-list">
            <Info label="From" value={email.sender?.email || 'Not available'} />
            <Info label="To" value={(email.recipients || []).map((recipient) => recipient.email).join(', ') || 'Not available'} />
            <Info label="CC" value={(email.cc || []).map((recipient) => recipient.email).join(', ') || 'Not provided'} />
            <Info label="Status" value={email.status || 'Not available'} />
            <Info label="Timestamp" value={email.timestamp ? new Date(email.timestamp).toLocaleString() : 'Not available'} />
          </div>
          <div className="tool-card" style={{ marginTop: 16 }}>
            <p style={{ whiteSpace: 'pre-wrap' }}>{email.body || 'No message body.'}</p>
          </div>
          {!!email.attachments?.length && (
            <div className="tool-card" style={{ marginTop: 16 }}>
              <h3>Attachments</h3>
              <p>{email.attachments.length} attachment(s)</p>
            </div>
          )}
        </section>
      )}
    </main>
  );
};

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="tool-row">
      <div className="tool-row-title">{label}</div>
      <div className="tool-row-sub">{value}</div>
    </div>
  );
}

export default EmailDetails;
